/* eslint-disable react/jsx-props-no-spreading */
import '../styles/globals.css';

import * as amplitude from '@amplitude/analytics-browser';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect, useState } from 'react';

import { SITE_ORIGIN } from '../modules/siteUrl';

const MyApp = ({ Component, pageProps }) => {
  const [isClient, setIsClient] = useState(false);
  const router = useRouter();

  let pageName;

  useEffect(() => {
    // Mark that we're on client-side
    setIsClient(true);

    // Initialize analytics only on client-side
    if (typeof window === 'undefined') return;

    try {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      if (user) {
        amplitude.init('27b620d609eeb9d1d0c15aa0aaeb95b1', user._id, {
          defaultTracking: true,
        });

        const identifyEvent = new amplitude.Identify();
        identifyEvent.set('companyName', user.companyName);
        identifyEvent.set('userType', user.type);

        amplitude.identify(identifyEvent);
      } else {
        amplitude.init('27b620d609eeb9d1d0c15aa0aaeb95b1', null, {
          defaultTracking: true,
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error initializing analytics:', error);
      amplitude.init('27b620d609eeb9d1d0c15aa0aaeb95b1');
    }
  }, []);

  // From the router rather than `window.location`: read off `window`, this ran
  // on the client only, so every page was server-rendered with the site-wide
  // default title and swapped to its real one on hydration. A crawler reads the
  // server's HTML, so every page looked like a duplicate of the home page.
  // `router.asPath` is the same path on both sides.
  {
    const pathName = router.asPath.split(/[?#]/)[0].split('/');
    pageName = pathName
      .pop()
      .replace(/-/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.substring(1))
      .join(' ');

    if (parseInt(pageName, 10)) {
      if (pathName.includes('ship')) {
        pageName = 'Edit Ship';
      } else if (pathName.includes('offer')) {
        pageName = 'View Offer';
      } else if (pathName.includes('service-request')) {
        pageName = 'View Service Request';
      } else if (pathName.includes('supplier')) {
        pageName = 'View Supplier Profile';
      } else if (pathName.includes('troubleshooting-request')) {
        pageName = 'View Troubleshooting Request';
      } else if (pathName.includes('requisition-request')) {
        pageName = 'View Requisition Request';
      } else if (pathName.includes('guides')) {
        pageName = 'Guides';
      }
    }
  }

  const finalPageName = pageName
    ? `${pageName} | bid2board`
    : 'bid2board | Digitalizing Marine Technical Service';

  const googleAnalyticsScript = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());

    try {
      const userId = localStorage.getItem('userId');
      const user = JSON.parse(localStorage.getItem('user') || 'null');

      // SITE_ORIGIN interpolated in, not a hardcoded apex: production is
      // www.bid2board.com, and the old startsWith("https://bid2board.com")
      // was never true there, so GA never received a user_id or a
      // company_name from the live site.
      if (user?.companyName !== 'Bid2board' && window.location.origin === '${SITE_ORIGIN}') {
        gtag('config', 'G-D06X6T3HK6', { user_id: userId });
        gtag('set', 'user_properties', { company_name: user ? user.companyName : '' });
      } else {
        gtag('config', 'G-D06X6T3HK6');
      }
    } catch (e) {
      console.error('Analytics error:', e);
      gtag('config', 'G-D06X6T3HK6');
    }
  `;

  return (
    <>
      <Head>
        <title>{finalPageName}</title>
        <meta property="og:title" content={finalPageName} key="og:title" />
        <meta property="og:type" content="article" key="og:type" />
        <meta
          property="og:description"
          content="bid2board ensures quality and efficiency, reduces the time required to arrange service and enhances visibility through a simple and automated platform"
          key="og:description"
        />
        <meta
          property="og:image"
          content={`${SITE_ORIGIN}/bid2board-social.png`}
          key="og:image"
        />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>

      {isClient && (
        <>
          <Script
            src="https://www.googletagmanager.com/gtag/js?id=G-D06X6T3HK6"
            strategy="afterInteractive"
          />
          <Script
            id="google-analytics"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{ __html: googleAnalyticsScript }}
          />
        </>
      )}

      <Component {...pageProps} />
    </>
  );
};

export default MyApp;
